import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Form, Input, Modal, notification, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getServicesCategories,
    changeServiceCategoriesOrder,
    createServicesCategories,
} from "../../../service";
import { ServiceCategorySection } from "../../molecules/menu-service-category-section/ServiceCategorySection";
import { colors, spacing } from "../../../styles/theme";

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.xl};
`;

const TabDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  max-width: 600px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

export const ServicesTab: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [categories, setCategories] = useState<any[]>([]);
    const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
    const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    const { isLoading, data: queryData } = useQuery<any, Error>(
        ["/services-categories"],
        () => getServicesCategories(),
        {
            onSuccess(data) {
                setCategories(
                    data.sort((a: any, b: any) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0))
                );
            },
        }
    );

    // Sync local state with query cache
    useEffect(() => {
        if (queryData && Array.isArray(queryData)) {
            setCategories((currentCategories) => {
                if (currentCategories.length === 0) {
                    return queryData.sort(
                        (a: any, b: any) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0)
                    );
                }
                const currentIds = currentCategories.map((c) => c.id);
                const updatedCategories = currentCategories.map((cat) => {
                    const updated = queryData.find((q: any) => q.id === cat.id);
                    return updated ? { ...cat, ...updated } : cat;
                });
                // Filter out categories that no longer exist
                const filteredCategories = updatedCategories.filter((cat) =>
                    queryData.some((q: any) => q.id === cat.id)
                );
                const newCategories = queryData.filter(
                    (q: any) => !currentIds.includes(q.id)
                );
                return [...filteredCategories, ...newCategories];
            });
        }
    }, [queryData]);

    const { mutateAsync: changeCategoryOrderMutation } = useMutation(
        changeServiceCategoriesOrder,
        {
            onSuccess: (data) => {
                setCategories(
                    data.sort((a: any, b: any) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0))
                );
                queryClient.invalidateQueries(["/services-categories"]);
            },
        }
    );

    const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
    const [addCategoryForm] = Form.useForm();

    const {
        mutateAsync: createCategoryMutation,
        isLoading: isCreatingCategory,
    } = useMutation(createServicesCategories, {
        onSuccess: async (data: any) => {
            notification.success({
                message: t("servicesTab.categoryCreatedSuccess"),
                style: { background: colors.successBackground },
                placement: "bottomRight",
            });
            addCategoryForm.resetFields();
            setAddCategoryModalOpen(false);

            // Refresh categories
            queryClient.invalidateQueries(["/services-categories"]);

            if (data?.id) {
                setNewCategoryId(data.id);
            }
        },
        onError: (error: any) => {
            notification.error({
                message: t("servicesTab.categoryCreateError"),
                description: error?.response?.data?.message || t("servicesTab.tryAgain"),
                style: { background: colors.errorBackground },
                placement: "bottomRight",
            });
        },
    });

    // Scroll to newly created category
    useEffect(() => {
        if (newCategoryId && categoryRefs.current[newCategoryId]) {
            setTimeout(() => {
                categoryRefs.current[newCategoryId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                setNewCategoryId(null);
            }, 100);
        }
    }, [newCategoryId, categories]);

    const handleMoveCategoryUp = async (categoryId: number) => {
        const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
        if (currentIndex <= 0) return;

        const newCategories = [...categories];
        [newCategories[currentIndex - 1], newCategories[currentIndex]] = [
            newCategories[currentIndex],
            newCategories[currentIndex - 1],
        ];

        const orderedIds = newCategories.map((cat) => cat.id);
        await changeCategoryOrderMutation(orderedIds);
    };

    const handleMoveCategoryDown = async (categoryId: number) => {
        const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
        if (currentIndex < 0 || currentIndex >= categories.length - 1) return;

        const newCategories = [...categories];
        [newCategories[currentIndex], newCategories[currentIndex + 1]] = [
            newCategories[currentIndex + 1],
            newCategories[currentIndex],
        ];

        const orderedIds = newCategories.map((cat) => cat.id);
        await changeCategoryOrderMutation(orderedIds);
    };

    const handleAddCategory = () => {
        setAddCategoryModalOpen(true);
    };

    const handleAddCategorySubmit = async () => {
        try {
            const values = await addCategoryForm.validateFields();
            await createCategoryMutation(values);
        } catch {
            // validation failed
        }
    };

    if (isLoading) {
        return <Spin spinning={true} />;
    }

    return (
        <>
            <TabHeader>
                <TabDescription>
                    {t("servicesTab.tabDescription")}
                </TabDescription>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCategory}
                    loading={isCreatingCategory}
                >
                    {t("servicesTab.addCategoryButton")}
                </Button>
            </TabHeader>

            <CategoriesContainer>
                {categories.map((category, index) => (
                    <div
                        key={category.id}
                        ref={(el) => (categoryRefs.current[category.id] = el)}
                    >
                        <ServiceCategorySection
                            category={category}
                            onMoveUp={() => handleMoveCategoryUp(category.id)}
                            onMoveDown={() => handleMoveCategoryDown(category.id)}
                            canMoveUp={index > 0}
                            canMoveDown={index < categories.length - 1}
                        />
                    </div>
                ))}
            </CategoriesContainer>

            <Modal
                title={t("servicesTab.addCategoryModalTitle")}
                open={addCategoryModalOpen}
                onOk={handleAddCategorySubmit}
                onCancel={() => {
                    addCategoryForm.resetFields();
                    setAddCategoryModalOpen(false);
                }}
                okText={t("servicesTab.createOk")}
                cancelText={t("servicesTab.cancel")}
                confirmLoading={isCreatingCategory}
                width={480}
            >
                <Form form={addCategoryForm} layout="vertical" autoComplete="off">
                    <Form.Item
                        name="name"
                        label={t("servicesTab.categoryNameLabel")}
                        rules={[{ required: true, message: t("servicesTab.categoryNameRequired") }]}
                    >
                        <Input placeholder={t("servicesTab.categoryNamePlaceholder")} autoFocus />
                    </Form.Item>
                    <Form.Item name="description" label={t("servicesTab.descriptionLabel")}>
                        <Input.TextArea placeholder={t("servicesTab.descriptionPlaceholder")} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
